import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import InlineSpinner from '../../shared/InlineSpinner';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM, INPUT_SM } from '../../shared/ui';
import { formatApiDateTime } from '../../../helpers/helpers';
import { extractRaceCsvFileMeta } from '../utils/athleteAssignmentsMeta';
import {
  ATHLETES_CSV_HEADER,
  parseAthletesCsv,
  serializeAthletesCsv,
  type AthleteCsvRow,
} from '../utils/athleteAssignmentsCsvParse';
import { dispatchToast, dispatchToastError, formatApiResponseError } from '../utils';
import { StageReadinessBadge } from './StageReadinessBadge';

function downloadTextFile(fileName: string, contentType: string, text: string): void {
  const blob = new Blob([text], { type: contentType || 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'athletes.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

type Props = {
  eventId: string;
  raceId: string;
  canEdit: boolean;
  hasAthletesRoster: boolean;
  /** Draw attention to the Edit button (e.g. DRAFT race with no roster yet). */
  pulseEdit?: boolean;
  /** Show the uploaded/not-uploaded badge (only meaningful pre-PACKAGING). */
  showReadyBadge?: boolean;
  /** Show the read-only roster table when not editing (DRAFT only — later stages show device pairing instead). */
  showReadOnlyView?: boolean;
  onSaved?: () => void | Promise<void>;
};

type AthleteRosterCardState = {
  /** Buttons for the hosting card's header (Edit, or Cancel/Save while editing). */
  headerRight: ReactNode;
  /** Read-only table when not editing (once a roster exists), or the editor while editing. */
  body: ReactNode;
  editing: boolean;
};

/** Headless roster-editor state so the caller can render it inside a shared card (with the device table). */
export function useAthleteRosterCard(props: Props): AthleteRosterCardState {
  const {
    eventId,
    raceId,
    canEdit,
    hasAthletesRoster,
    pulseEdit,
    showReadyBadge,
    showReadOnlyView = true,
    onSaved,
  } = props;

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<AthleteCsvRow[]>([]);
  const [meta, setMeta] = useState<Record<string, unknown> | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Switching races (or closing the workspace) discards any in-progress edit.
    setEditing(false);
  }, [eventId, raceId]);

  const metaSummary = useMemo(() => {
    if (!meta) return null;
    const uploadedAt = meta.uploadedAt ?? meta.createdAt;
    const by = meta.uploadedBy ?? meta.createdBy;
    const parts: string[] = [];
    if (uploadedAt != null) parts.push(formatApiDateTime(uploadedAt));
    if (typeof by === 'string' && by.trim()) parts.push(`by ${by.trim()}`);
    return parts.length ? parts.join(' ') : null;
  }, [meta]);

  const loadFromApi = useCallback(async (): Promise<void> => {
    if (!window.eventsAPI) {
      dispatchToast('toast-error', 'Events API is not available');
      return;
    }
    setLoading(true);
    try {
      const [metaRes, csvRes] = await Promise.all([
        window.eventsAPI.getAthletesCsvMeta(eventId, { raceId }),
        window.eventsAPI.getAthletesCsv(eventId, { raceId }),
      ]);
      setMeta(extractRaceCsvFileMeta(metaRes));
      if (csvRes?.ok && csvRes.data?.csvText) {
        const parsed = parseAthletesCsv(String(csvRes.data.csvText));
        if (parsed.error) {
          dispatchToast('toast-error', parsed.error);
          setRows([]);
        } else {
          setRows(parsed.rows);
        }
      } else {
        setRows([]);
      }
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to load athlete list');
    } finally {
      setLoading(false);
    }
  }, [eventId, raceId]);

  useEffect(() => {
    // Load rows for the read-only view whenever a roster exists for this race.
    if (!showReadOnlyView || !hasAthletesRoster) {
      setRows([]);
      setMeta(null);
      return;
    }
    void loadFromApi();
  }, [showReadOnlyView, hasAthletesRoster, loadFromApi]);

  const startEdit = useCallback(async () => {
    setEditing(true);
    await loadFromApi();
  }, [loadFromApi]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (!window.eventsAPI) return;
    const parsed = parseAthletesCsv(serializeAthletesCsv(rows));
    if (parsed.error) {
      dispatchToast('toast-error', parsed.error);
      return;
    }
    setSaving(true);
    try {
      const res = await window.eventsAPI.submitAthletesCsv(eventId, {
        raceId,
        csvText: serializeAthletesCsv(parsed.rows),
        fileName: 'athletes.csv',
        contentType: 'text/csv',
      });
      if (!res?.ok) {
        dispatchToast('toast-error', formatApiResponseError(res, 'Failed to save athlete list'));
        return;
      }
      dispatchToast('toast-info', 'Athletes list saved');
      setEditing(false);
      await Promise.resolve(onSaved?.());
    } catch (err: unknown) {
      dispatchToastError(err, 'Failed to save athlete list');
    } finally {
      setSaving(false);
    }
  }, [eventId, raceId, rows, onSaved]);

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const parsed = parseAthletesCsv(text);
      if (parsed.error) {
        dispatchToast('toast-error', parsed.error);
        return;
      }
      setRows(parsed.rows);
      dispatchToast('toast-info', `Loaded ${parsed.rows.length} athletes from file`);
    };
    reader.onerror = () => dispatchToast('toast-error', 'Could not read file');
    reader.readAsText(file);
  }

  function updateRow(index: number, patch: Partial<AthleteCsvRow>): void {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow(): void {
    setRows((prev) => [...prev, { athleteName: '', athleteId: '', athleteRaceNo: '' }]);
  }

  function removeRow(index: number): void {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  const readyBadge = showReadyBadge ? (
    <StageReadinessBadge ok={hasAthletesRoster} okLabel="Uploaded" missingLabel="Not uploaded" />
  ) : null;

  const headerRight = (
    <div className="flex items-center gap-2">
      {editing ? (
        <>
          <button type="button" onClick={cancelEdit} disabled={saving} className={BTN_SECONDARY_SM}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={loading || saving || rows.length === 0}
            title={rows.length === 0 ? 'Add at least one athlete row first' : undefined}
            className={`${BTN_PRIMARY_SM} inline-flex items-center gap-2`}
          >
            {saving ? (
              <>
                <InlineSpinner size="xs" variant="current" />
                Saving…
              </>
            ) : (
              'Save'
            )}
          </button>
        </>
      ) : (
        <>
          {readyBadge}
          {canEdit ? (
            <button
              type="button"
              onClick={() => void startEdit()}
              className={[BTN_PRIMARY_SM, pulseEdit ? 'animate-pulse ring-2 ring-blue-300/90 dark:ring-blue-500/80' : ''].join(' ')}
            >
              Edit athlete list
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  const readOnlyTable = showReadOnlyView && hasAthletesRoster && rows.length > 0 ? (
    <div className="space-y-2">
      {metaSummary ? (
        <p className="text-[11px] text-gray-500 dark:text-slate-400">Last uploaded {metaSummary}</p>
      ) : null}
      <div className="max-h-[min(40vh,400px)] overflow-auto rounded-lg border border-gray-200 dark:border-slate-700">
        <table className="w-full min-w-[420px] border-collapse text-sm">
          <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800 text-left text-xs font-semibold text-gray-700 dark:text-slate-300">
            <tr>
              <th className="px-2 py-2">Athlete name</th>
              <th className="px-2 py-2">Athlete ID</th>
              <th className="px-2 py-2">Race #</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
            {rows.map((row, i) => (
              <tr key={`${row.athleteId}-${i}`} className="bg-white dark:bg-slate-900">
                <td className="px-2 py-1.5 text-gray-900 dark:text-slate-100">{row.athleteName}</td>
                <td className="px-2 py-1.5 font-mono text-gray-700 dark:text-slate-300">{row.athleteId}</td>
                <td className="px-2 py-1.5 font-mono text-gray-700 dark:text-slate-300">{row.athleteRaceNo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  ) : null;

  const body = !editing ? (loading ? (
    <div className="flex items-center justify-center py-4">
      <InlineSpinner size="sm" />
    </div>
  ) : readOnlyTable) : loading ? (
    <div className="flex items-center justify-center py-8">
      <InlineSpinner />
    </div>
  ) : (
    <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-gray-500 dark:text-slate-400">
              Columns: <code className="text-[10px]">{ATHLETES_CSV_HEADER}</code>
              {metaSummary ? ` · Last uploaded ${metaSummary}` : ''}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                disabled={saving}
                onClick={() => fileInputRef.current?.click()}
              >
                Upload CSV
              </button>
              <button
                type="button"
                className={BTN_SECONDARY_SM}
                disabled={saving}
                onClick={() => downloadTextFile('athletes.csv', 'text/csv', serializeAthletesCsv(rows))}
              >
                Download CSV
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileUpload}
              />
            </div>
          </div>

          <div className="max-h-[min(40vh,400px)] overflow-auto rounded-lg border border-gray-200 dark:border-slate-700">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-slate-800 text-left text-xs font-semibold text-gray-700 dark:text-slate-300">
                <tr>
                  <th className="px-2 py-2">Athlete name</th>
                  <th className="px-2 py-2">Athlete ID</th>
                  <th className="px-2 py-2">Race #</th>
                  <th className="px-2 py-2 w-16" aria-label="Actions" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {rows.map((row, i) => (
                  <tr key={`${row.athleteId}-${i}`} className="bg-white dark:bg-slate-900">
                    <td className="px-2 py-1">
                      <input
                        className={INPUT_SM}
                        value={row.athleteName}
                        disabled={saving}
                        onChange={(e) => updateRow(i, { athleteName: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        className={`${INPUT_SM} font-mono`}
                        value={row.athleteId}
                        disabled={saving}
                        onChange={(e) => updateRow(i, { athleteId: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        className={`${INPUT_SM} font-mono`}
                        value={row.athleteRaceNo}
                        disabled={saving}
                        onChange={(e) => updateRow(i, { athleteRaceNo: e.target.value })}
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline disabled:opacity-40"
                        disabled={saving}
                        onClick={() => removeRow(i)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-2 py-4 text-center text-xs text-gray-500 dark:text-slate-400">
                      No athletes yet. Add a row or upload a CSV.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <button type="button" className={BTN_SECONDARY_SM} disabled={saving} onClick={addRow}>
            Add athlete row
          </button>
        </div>
  );

  return { headerRight, body, editing };
}
